import { Link } from "react-router-dom";
import { formatDate, formatDateTime } from "../lib/dateFormat";

import PlayerWhatsappGroupsButton from "./PlayerWhatsappGroupsButton";
import RankMovementBadge from "./RankMovementBadge";
import type { PlayerDashboardTournamentCard } from "../lib/api";
import { participantStatusLabel, tournamentStatusLabel, tournamentTypeLabel } from "../lib/playerLabels";
import { PLAYER_STAGE_NEXT_MATCH_MESSAGE, playerStageLocked } from "../lib/playerStage";
import { isSchedulePendingForPlayer, SCHEDULE_PENDING_LABEL } from "../lib/matchScheduleStatus";

export default function PlayerTournamentCard({
  card,
}: {
  card: PlayerDashboardTournamentCard;
}) {
  const ruleLabel = !card.rule_acceptance.rule_published
    ? "Sin reglamento publicado"
    : card.rule_acceptance.accepted
      ? `Reglamento aceptado (v${card.rule_acceptance.rule_version ?? "?"})`
      : `Aceptación pendiente (v${card.rule_acceptance.rule_version ?? "?"})`;
  // En Round Robin el sorteo es de grupos (también el planeado en Datos).
  const drawLabel =
    card.draw_ceremony?.kind === "rr_groups" || card.draw_ceremony?.method === "rr_groups"
      ? "Sorteo de grupos"
      : "Sorteo del cuadro";

  return (
    <article className="tournament-card player-tournament-card">
      <h3 className="tournament-card__title">
        {card.tournament_name ?? `Torneo ${card.tournament_id}`}
      </h3>
      <div className="tournament-card__row">
        <span className="muted">Estado</span>
        <span>{tournamentStatusLabel(card.tournament_status)}</span>
      </div>
      {card.final_result ? (
        <div className="tournament-card__row">
          <span className="muted">Resultado final</span>
          <strong>{card.final_result.label}</strong>
        </div>
      ) : null}
      <div className="tournament-card__row">
        <span className="muted">Tipo</span>
        <span>{tournamentTypeLabel(card.tournament_type)}</span>
      </div>
      <div className="tournament-card__row">
        <span className="muted">Inscripción</span>
        <span>{participantStatusLabel(card.participant_status)}</span>
      </div>
      <div className="tournament-card__row">
        <span className="muted">Rank inicial</span>
        <span>{card.initial_rank ?? card.tournament_rank ?? "—"}</span>
      </div>
      <div className="tournament-card__row">
        <span className="muted">Rank actual</span>
        <span>
          {card.current_rank ?? "—"}{" "}
          <RankMovementBadge
            movement={card.rank_movement}
            delta={card.rank_delta}
          />
        </span>
      </div>
      {card.next_match ? (
        <dl className="next-match-strip">
          <dt>Próximo</dt>
          <dd>
            {card.next_match.player1?.display_name ?? "Tú"} vs{" "}
            {card.next_match.player2?.display_name ?? "Por definir"}
          </dd>
          <dt>Cuándo</dt>
          <dd>
            {/* Publicar horarios: el servidor no manda el horario aún sin publicar. */}
            {formatDate(
              card.next_match.scheduled_date,
              isSchedulePendingForPlayer(card.next_match) ? SCHEDULE_PENDING_LABEL : "Sin fecha",
            )}
            {card.next_match.scheduled_time
              ? ` ${card.next_match.scheduled_time}`
              : ""}
          </dd>
        </dl>
      ) : playerStageLocked(card.player_visibility) ? (
        <p className="muted" style={{ margin: 0 }} data-no-toast>
          {PLAYER_STAGE_NEXT_MATCH_MESSAGE}
        </p>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          Sin partidos próximos programados.
        </p>
      )}
      <div className="tournament-card__row">
        <span className="muted">Reglamento</span>
        <span>{ruleLabel}</span>
      </div>
      {card.draw_ceremony?.performed_at ? (
        <div className="tournament-card__row">
          <span className="muted">{drawLabel}</span>
          <span>
            Realizado ·{" "}
            <Link to={`/player/tournaments/${card.tournament_id}/sorteo`}>ver resultado</Link>
          </span>
        </div>
      ) : card.draw_ceremony?.scheduled_at ? (
        <div className="tournament-card__row">
          <span className="muted">{drawLabel}</span>
          <span>🎲 {formatDateTime(card.draw_ceremony.scheduled_at)} h (CDMX)</span>
        </div>
      ) : null}
      {card.whatsapp_groups_after_payment ? (
        <p className="muted" style={{ margin: 0 }}>
          El grupo de WhatsApp del torneo se te compartirá al confirmar tu pago.
        </p>
      ) : null}
      <div className="tournament-card__actions">
        <Link
          className="btn btn--ghost btn--compact"
          to={`/player/tournaments/${card.tournament_id}`}
        >
          Entrar
        </Link>
        <Link
          className="btn btn--ghost btn--compact"
          to={`/player/tournaments/${card.tournament_id}/bracket`}
        >
          ✅ Draw
        </Link>
        <Link
          className="btn btn--ghost btn--compact"
          to={`/player/tournaments/${card.tournament_id}/groups`}
        >
          ▦ Grupos
        </Link>
        <Link
          className="btn btn--ghost btn--compact"
          to={`/player/tournaments/${card.tournament_id}/standings`}
        >
          🏆 Ranking
        </Link>
        <Link
          className="btn btn--ghost btn--compact"
          to={`/player/tournaments/${card.tournament_id}/communications`}
        >
          📣 Comunicados
        </Link>
        <Link
          className="btn btn--ghost btn--compact"
          to={`/player/tournaments/${card.tournament_id}/rules`}
        >
          📜 Reglamento
        </Link>
        {/* Eduardo 2026-09-15: inscribir a un familiar a ESTE torneo desde
            la tarjeta; el link público reconoce la sesión y ofrece los
            perfiles familiares. */}
        {card.tournament_slug && card.tournament_is_public !== false && !["archived", "closed", "finished", "completed", "cancelled"].includes(card.tournament_status ?? "") ? (
          <Link
            className="btn btn--ghost btn--compact"
            to={`/inscripcion/${card.tournament_slug}`}
            title="Inscribe a un hijo o familiar a este torneo con tu cuenta"
          >
            👨‍👩‍👧 Inscribir familiar
          </Link>
        ) : null}
        {/* Eduardo 2026-10-01: el grupo de WhatsApp que el organizador puso
            en Datos, solo para el inscrito (lo filtra el servidor) y sin
            esperar a que el torneo esté armado. */}
        <PlayerWhatsappGroupsButton
          groups={card.whatsapp_groups}
          className="btn btn--ghost btn--compact player-whatsapp-button"
        />
      </div>
    </article>
  );
}
